import { Button, Flex, Typography, Tabs, Spin } from "antd";
import { useQuery } from "@tanstack/react-query";
import CardSectionTitle from "components/CardSectionTitle.component";
import { translate, translateWithVars } from "shared/languages";
import { FilePdfOutlined, FileTextOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import { getCustomerPackageById } from "hooks/queries/customerpackage";
import { useGetUserById } from "hooks/queries/user";
import { useTabParam } from "hooks/useTabParam";
import { useParams } from "react-router-dom";
import BackPageHeading from "components/BackPageHeading.component";
import { RestrictedBanner } from "components/capability";
import PATHS from "shared/constants/paths";
import CustomerBookingTable from "components/bookings/CustomerBookingTable";
import { TabsProps } from "antd/lib";
import { UpgradeCustomerPackage } from "./UpgradeCustomerPackage";
import PackageHistoryTable from "./PackageHistoryTable";
import { CustomerPackageCard } from "components/CustomerPackageCard.component";
import { bookingsByCustomerPackageV2Options } from "hooks/queries/booking-v2";
import { useAuth } from "context/AuthContextProvider";
import { UserType } from "shared/types/enum";
import { downloadPurchaseDocument, purchaseReceiptFromParts } from "shared/utils/purchaseDocuments";
import { discountSourceOf } from "shared/utils/purchaseDiscount";
import { amountPaid, rewardSourceOf } from "shared/utils";
import { localTzDate } from "shared/utils/dayjs";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

const CustomerPackageDetailsPage: React.FC = () => {
  const { customerPackageId } = useParams();
  // The header slot the package card renders its Void / Batalkan Upgrade
  // button into (a callback ref into state, so the card re-renders once the
  // slot exists). Left of the two download buttons: the one unbordered item
  // in the row, read as "two documents and one reversal action".
  const [saleActionsSlot, setSaleActionsSlot] = useState<HTMLSpanElement | null>(null);
  const { state: authState } = useAuth();
  const { data: customerPackage, isLoading } =
    getCustomerPackageById(customerPackageId);
  const { data: customer } = useGetUserById(
    customerPackage?.customer_id ?? "",
    UserType.Customer,
    !!customerPackage?.customer_id
  );
  const { data: bookings = [], isLoading: isBookingLoading } = useQuery(
    bookingsByCustomerPackageV2Options(customerPackageId)
  );

  // A membership cannot be upgraded (the server's upgrade targets are credit
  // tiers, and UpgradeCustomerPackage refuses `unlimited`), so on a membership
  // the Upgrade tab only ever showed a disabled button. Keep it when the tab
  // still has something to show: the package this one replaced, or the one it
  // was replaced BY. The second case was added with the "Diupgrade Menjadi"
  // card (2026-09-21); without it a membership that had been upgraded away
  // would hide the only surface naming its replacement. Reaching that state
  // needs a membership to be an upgrade SOURCE, which the catalogue makes
  // practically impossible, but the gate should not be the reason it breaks.
  const isMembership = !!customerPackage?.package_deal.unlimited;
  const showUpgradeTab =
    !isMembership ||
    !!customerPackage?.previous_customer_package ||
    !!customerPackage?.new_customer_package;
  const tabKeys = showUpgradeTab ? ["1", "2", "3"] : ["1", "3"];
  const { activeKey, onChange } = useTabParam("1", "tab", tabKeys);

  // A claimed reward credit was never sold: no payment, no transaction number,
  // so no invoice or receipt either (the document would bill Rp 0 with no
  // number). The heading says where it came from and when it was claimed
  // instead; the claim is the row's purchase_date.
  const rewardSource = rewardSourceOf(customerPackage?.package_deal);

  // Invoice/Receipt downloads live here (the detail page every purchase row
  // navigates to) instead of as icon-only buttons on the list tables — those
  // gave no visual cue which icon was which document.
  const handleDownload = (kind: "invoice" | "receipt") => () => {
    if (!customerPackage || !customer) return;
    // An upgrade is a modification of an existing sale, so the document has to
    // bill the difference the client actually paid, never the new package's
    // full catalogue price (which is what purchase_price carries). The document
    // explains that amount by showing the new package at full value less a
    // credit for the old one, so the line item is just the package name here,
    // exactly as on an ordinary purchase. It carried an "Upgrade ke:" prefix
    // briefly; the Paket Lama row already says what happened, the way a
    // trade-in line does, so the prefix was saying it twice and cost the row
    // the width it needed to fit on one line.
    const previousPackage = customerPackage.previous_customer_package;
    const data = purchaseReceiptFromParts({
      transactionNumber: customerPackage.transaction_number,
      business: authState.business,
      customerName: customer.full_name,
      customerPhone: customer.phone_number,
      customerEmail: customer.email,
      itemName: customerPackage.package_deal.name,
      price: amountPaid(customerPackage),
      // purchase_price on an upgrade row is the new package's full value,
      // stamped at upgrade time under the same tax settings as the fee.
      upgradeFullPrice: previousPackage ? customerPackage.purchase_price : null,
      upgradeFromName: previousPackage?.package_deal.name ?? null,
      upgradePreviousPrice: previousPackage
        ? customerPackage.upgrade_previous_price ?? null
        : null,
      purchasedAt: String(customerPackage.purchase_date),
      paymentMethodName: customerPackage.payment_method_name,
      // Every source (code, loyalty, referral); discountSourceOf also covers
      // an older server that only sent code discounts.
      discountSource: discountSourceOf(customerPackage),
      discountCode: customerPackage.discount_code,
      discountPercent: customerPackage.discount_percent,
      discountAmount: customerPackage.discount_amount,
      originalPrice: customerPackage.original_price,
      taxEnabled: customerPackage.tax_enabled_snapshot,
      taxMode: customerPackage.tax_mode_snapshot,
      taxRate: customerPackage.tax_rate_snapshot,
      taxLabel: customerPackage.tax_label_snapshot,
      voidedAt: customerPackage.voided_at,
    });
    downloadPurchaseDocument(kind, data, authState.business?.name ?? "");
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: translate("tabs.bookings"),
      children: (
        <Flex vertical gap={10}>
          <CardSectionTitle title={`Booking (${bookings.length})`} />
          <CustomerBookingTable
            bookings={bookings}
            isLoading={isLoading || isBookingLoading}
            sortAscending={false}
            showSearchStaff={false}
            membership={!!customerPackage?.package_deal.unlimited}
            // This list is the PACKAGE's bookings, not one client's, so once
            // the package is shared it holds several people's rows.
            showAttendee={(customerPackage?.members ?? []).some(
              (m) => !m.deleted_at,
            )}
          />
        </Flex>
      ),
    },
    ...(showUpgradeTab
      ? [
          {
            key: "2",
            label: "Upgrade",
            children: customerPackage && (
              <UpgradeCustomerPackage customerPackage={customerPackage} />
            ),
          },
        ]
      : []),
    {
      key: "3",
      label: translate("tabs.history"),
      children: (
        <PackageHistoryTable
          customerPackageId={customerPackageId}
          members={customerPackage?.members}
        />
      ),
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <BackPageHeading
        // Opened from the owner's Paket tab, a co-sharer's, Transaksi, the
        // Upgrade tab and the Pembelian / Pemakaian Reward reports, so the
        // arrow goes back to whichever it was. The owner's list is only the
        // fallback for a package opened in a fresh tab.
        historyBack
        backTo={
          customerPackage
            ? `${PATHS.CUSTOMER}/${customerPackage.customer_id}?tab=${
                customerPackage.package_deal.unlimited ? "4" : "2"
              }`
            : PATHS.CUSTOMER
        }
        title={translate(
          customerPackage?.package_deal.unlimited
            ? "title.customer_memberships"
            : "title.customer_packages"
        )}
        extra={
          customerPackage && rewardSource ? (
            <Typography.Text style={{ fontSize: textPx(14), color: colors.ink2 }}>
              {translateWithVars(`customer_package.reward_claimed_${rewardSource}`, {
                date: localTzDate(customerPackage.purchase_date).format("D MMM YYYY"),
              })}
            </Typography.Text>
          ) : (
            customerPackage &&
            customer && (
              <Flex gap={8} align="center">
                {/* Empty when the sale has no action. */}
                <span ref={setSaleActionsSlot} style={{ display: "contents" }} />
                <Button icon={<FileTextOutlined />} onClick={handleDownload("invoice")}>
                  {translate("paymentMethods.download_invoice")}
                </Button>
                <Button icon={<FilePdfOutlined />} onClick={handleDownload("receipt")}>
                  {translate("paymentMethods.download_receipt")}
                </Button>
              </Flex>
            )
          )
        }
      />
      {/* The owner is deactivated at this studio. The package stays
          editable while a co-sharer still uses it,
          and is read-only once nobody active is left. */}
      {customerPackage?.owner_active === false && (
        <div style={{ marginBottom: 16 }}>
          <RestrictedBanner
            title={
              customerPackage.in_use
                ? translate("client_status.package_banner_in_use")
                : translate("client_status.package_banner_locked")
            }
          />
        </div>
      )}
      {customerPackage && (
        <CustomerPackageCard
          customerPackage={customerPackage}
          actionsContainer={saleActionsSlot}
          // Is recently upgraded if there is a previous package but no new package
          recentlyUpgraded={
            !!customerPackage.previous_customer_package &&
            !!!customerPackage.new_customer_package
          }
        />
      )}
      <Tabs
        activeKey={activeKey}
        size="small"
        items={items}
        onChange={onChange}
      />
    </Spin>
  );
};

export default CustomerPackageDetailsPage;
